import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { NotificationRedirectionOption, NotificationRequestBody } from '../../features/notification/notificationTypes';
import { fetchNotificationRedirectionOptions, sendNotificationInChunks, triggerNotification } from '../../services/notificationService';
import { getGlobalFilters, uploadImages, validateGoogleUrl } from '../../services/bannerServices';
import { openPopup } from '../../stateManagement/actions/popupActions';
import './NotificationForm.css';
import { isEmpty } from 'lodash';
import NotificationAdditionalComponents from '../NotificationAdditionalComponents/NotificationAdditionalComponents';
import ReactQuill from 'react-quill';
import { useLocation, useNavigate } from 'react-router-dom';
import 'react-quill/dist/quill.snow.css';
import DefaultHeading from '../heading/DefaultHeading';
import GenericFileUpload from '../genericFileUpload/GenericFileUpload';
import GenericFilters from '../genericFilters/genericFilters';
import { getCurrentGlobalFilterObject } from '../../stateManagement/actions/genericFilterActions';
import { ConfirmationPopUp } from '../popup/confirmationPopUp';
import { TranslationEnum, usePortalTranslation } from '../../providers/TranslationProvider';

function NotificationForm(props:{useTrigger?:boolean}) {
    const { translate } = usePortalTranslation();
    const CUR_COMPONENT = "notification"
    const CUR_COMPONENT_COMMON="commonPortal";
    const location = useLocation();
    const { state } = location;
    const users:any[] = state as any[];
    const navigate = useNavigate();
    const [heading, setHeading] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [notificationImage, setNotificationImage] = useState<File | null>(null);
    const [notificationImageUrl, setNotificationImageUrl] = useState<string>("");
    const [mediaUrl, setMediaUrl] = useState<string>("");
    const [showSubmitLoader, setShowSubmitLoader] = useState<boolean>(false);
    const additionalNotificationData = useRef<any>({});
    const quillRef = useRef<ReactQuill>();
    const [notificationRedirectionOptions, setNotficationRedirectionOptions] = useState<NotificationRedirectionOption[]>([]);
    const defaultNotifcationType = useRef<NotificationRedirectionOption>({
        id: "notification",
        label: "Notification Section"
    });
    const [notificationTarget, setNotificationTarget] = useState<NotificationRedirectionOption>(defaultNotifcationType.current);
    const [resetFiltersFlag, setResetFiltersFlag] = React.useState<boolean>(false);
    const [genericFilter,setGenericFilter] = React.useState<any[]>([]);
    const [openConfirmPopup,setOpenConfirmPopup] = useState<boolean>(false);

    useEffect(() => {
        const getAndSetNotificationRedirectionOptions = async () => {
            const options = await fetchNotificationRedirectionOptions();
            setNotficationRedirectionOptions(options);
            const defaultTarget = options.find((option: NotificationRedirectionOption) => option.id === 'notification');
            defaultNotifcationType.current = defaultTarget ?? options[0]
            setNotificationTarget(defaultNotifcationType.current);
        }
        const getAndSetGlobalFilters = async ()=>{
                const filters = await getGlobalFilters("notificationGlobalFilter");
                if(filters.length){
                    setGenericFilter(filters);
                }
                if((!users || users.length === 0) && !filters.length){
                    navigate('/notification')
                }
        }
        if(props.useTrigger){
            getAndSetGlobalFilters();
        }else if(!users || users.length === 0){
            navigate('/notification');
        }
        getAndSetNotificationRedirectionOptions();
    }, [])

    const resetNotificationPage = ()=>{
        setResetFiltersFlag(!resetFiltersFlag); 
        setNotificationTarget(defaultNotifcationType.current);
        additionalNotificationData.current = {};
        setDescription('')
        setHeading('')
        setNotificationImage(null);
        setMediaUrl('')
    }

    const getConvertedDescriptionString = () => {
	let htmlStr = "";
	quillRef.current?.getEditor().getContents().ops?.forEach((item:any) => {
		let closingTags = "";
		if (!item.attributes) {
			htmlStr += item.insert;
		} else {
			if (item.attributes.color) {
				htmlStr += `<font color="${item.attributes.color}">`;
				closingTags = "</font>" + closingTags;
			}
			if (item.attributes.bold) {
				htmlStr += "<b>";
				closingTags = "</b>" + closingTags;
			}
			if (item.attributes.italic) {
				htmlStr += "<i>";
				closingTags = "</i>" + closingTags;
			}
			if (item.attributes.underline) {
				htmlStr += "<u>";
				closingTags = "</u>" + closingTags;
			}
			if (item.attributes.strike) {
				htmlStr += "<s>";
				closingTags = "</s>" + closingTags;
			}

			htmlStr += item.insert;
			htmlStr += closingTags;
		}
	});

	return htmlStr === '\n' ? "" : htmlStr;
}

    const validAllInputs = () => {
        let message = "";
        let invalidCount = 0;
        if (!heading.trim()) {
            message = "Please enter a heading";
            invalidCount++;
        }
        const convertedDescripitonHtml = getConvertedDescriptionString();

        if (!convertedDescripitonHtml || !convertedDescripitonHtml.trim()) {
            message = "Please enter a description";
            invalidCount++;
        }
        if (mediaUrl && !validateGoogleUrl(mediaUrl)) {
            message = "Please enter a valid URL";
            invalidCount++;
        }
        const additionalDataValidationMessage = additionalDataValidation();
        if(additionalDataValidationMessage){
            message = additionalDataValidationMessage;
            invalidCount++;
        }
        return invalidCount < 2 ? message : "Multiple required fields are invalid";
    }

    const additionalDataValidation = () =>{
        switch(notificationTarget.id){
            case "skuSelection":{
                if(isEmpty(additionalNotificationData.current)){
                    return "Select atleast one SKU";
                }
                for(const key in additionalNotificationData.current){
                    const skuObject = additionalNotificationData.current[key];
                    const pieceValue = parseInt(skuObject.piece,10)
                    const caseValue = parseInt(skuObject.case,10)
                    if(!pieceValue && !caseValue){
                        return "Enter either case or piece value for " + key;
                    }
                }
                return "";
            }
            case "CategoryWiseProduct":{
                if( additionalNotificationData.current.category && additionalNotificationData.current.category.length ===0 &&
                    additionalNotificationData.current.pieceSize && additionalNotificationData.current.pieceSize.length ===0  &&
                    additionalNotificationData.current.brand && additionalNotificationData.current.brand.length ===0 
                    ){
                        return "Select atleast one filter";
                }
                return "";
            }
            default : return "";
        }
    }

    const getAdditionalData = ()=>{
        switch(notificationTarget.id){
            case "skuSelection":{
                const additionalData:any = {};
                Object.keys(additionalNotificationData.current).forEach((skuDescriptionCode)=>{
                    const skuDescriptionCodeArray = skuDescriptionCode.split('-');
                    const skuCode = skuDescriptionCodeArray[skuDescriptionCodeArray.length-1].trim();
                    additionalData[skuCode] = additionalNotificationData.current[skuDescriptionCode];
                    additionalData[skuCode].piece = parseInt(additionalData[skuCode].piece, 10) ?  parseInt(additionalData[skuCode].piece, 10).toString() : '0';
                    additionalData[skuCode].case = parseInt(additionalData[skuCode].case, 10) ?  parseInt(additionalData[skuCode].case, 10).toString() : '0';
                })
                return JSON.stringify(additionalData);
            }
            case "CategoryWiseProduct":{
                return `${additionalNotificationData.current.category}@@${additionalNotificationData.current.pieceSize}@@${additionalNotificationData.current.brand}`;
            }
            default : return "";
        }
    }

    const prepareNotificationBody = async () => {
        let blobUrls: string[] | { message: string } = [];
        if (notificationImage) {
            const imagesPayload = new FormData();
            imagesPayload.append("files", notificationImage);
            blobUrls = await uploadImages(imagesPayload);
            if (!Array.isArray(blobUrls)) {
                setShowSubmitLoader(false);
                openPopup("Alert", blobUrls.message);
                return;
            }
        }
        const userNotificationArray = users.map((user) => {
            if(!user.loginId){
                return null;
            }
            const notificationPayload: NotificationRequestBody = {
                "recipient": [user.loginId],
                "payload": { 
                    "subject": heading, 
                    "subcategory": heading, 
                    "userName": user.loginId, 
                    "category": heading, 
                    "description": getConvertedDescriptionString(), 
                    "feature": notificationTarget.id 
                },
                "profileName": "firebase",
                "lob": JSON.parse(localStorage.authContext).user.lob,
                "type":"firebase"
            };
            if (mediaUrl) {
                notificationPayload.payload.mediaUrl = mediaUrl;
            }

            if( !isEmpty(additionalNotificationData.current) ){
                notificationPayload.payload.data = getAdditionalData();
            }
            blobUrls = blobUrls as string[];
            if (notificationImage && blobUrls?.length === 1) {
                notificationPayload.payload.blobKey = blobUrls[0];
            }
            return notificationPayload;
        }).filter(Boolean);
        return userNotificationArray as NotificationRequestBody[];
    }

    const prepareNotificationBodyForSmartTrigger = async () => {
        let blobUrls: string[] | { message: string } = [];
        if (notificationImage) {
            const imagesPayload = new FormData();
            imagesPayload.append("files", notificationImage);
            blobUrls = await uploadImages(imagesPayload);
            if (!Array.isArray(blobUrls)) {
                setShowSubmitLoader(false);
                openPopup("Alert", blobUrls.message);
                return;
            }
        }

        const notificationReqBody:any = {
            "outletCode": "",
            "subHeading": heading,
            "subject": notificationTarget.id ,
            "description": getConvertedDescriptionString(),
            "feature": notificationTarget.id,
        }

        if(users && users.length){
            notificationReqBody.parent = users.map(user=>user.loginId) + "";
        }

        if(genericFilter.length){
            const selectedGlobalFilters = getCurrentGlobalFilterObject();
            Object.assign(notificationReqBody,selectedGlobalFilters)
            if(notificationReqBody.loginId){
                notificationReqBody.parent = notificationReqBody.loginId;
                delete notificationReqBody.loginId;
            }
        }

        if (mediaUrl) {
            notificationReqBody.mediaUrl = mediaUrl;
        }

        if( !isEmpty(additionalNotificationData.current) ){
            notificationReqBody.data = getAdditionalData();
        }
        blobUrls = blobUrls as string[];
        if (notificationImage && blobUrls?.length === 1) {
            notificationReqBody.blobKey = blobUrls[0];
        }

        return notificationReqBody;
    }

    const sendNotification = async () => {
        try {
            setShowSubmitLoader(true);
            const validationMessage = validAllInputs();
            if (validationMessage !== "") {
                setShowSubmitLoader(false);
                openPopup("Alert", validationMessage);
                return;
            }

            const userNotificationReqBody = (props.useTrigger && genericFilter.length) ? await prepareNotificationBodyForSmartTrigger() : await prepareNotificationBody();

            if(!userNotificationReqBody){
                return;
            }

            const failureCount = (props.useTrigger && genericFilter.length) ? await triggerNotification(userNotificationReqBody) : await sendNotificationInChunks(userNotificationReqBody);
            setShowSubmitLoader(false);
            if(!failureCount){
                resetNotificationPage();
                openPopup("Success", "Notification send successfully");
            }else{
                openPopup("Error", "Notifications not sent to " + failureCount + " users");
            }
        } catch (err) {
            console.log(err);
            setShowSubmitLoader(false);
            openPopup("Error", "An Error occured while sending notification");
        }
    }

    const handleNotificationFeatureSelect = (event: React.SyntheticEvent<Element, Event> | null, value: NotificationRedirectionOption) => {
        setNotificationTarget(value);
        additionalNotificationData.current = {};
        if (value.id !== "notification") {
            setMediaUrl("");
        }
    }
    const handleQuill = (instance:ReactQuill) => {
        if (instance && quillRef.current !== instance) {
            quillRef.current = instance;
        }
      };

    return (
        <div className='push-notification-container'>
            {genericFilter && genericFilter.length!==0 &&
                <div className="push-notification-inner-container" >
                    <DefaultHeading heading={translate(TranslationEnum.notification,"Notification Filters")} showDefaultBackButton={true} />
                    <Box className="generic-filter-container">
                        <GenericFilters filterOptions={genericFilter} resetFiltersFlag={resetFiltersFlag} />
                    </Box>
                </div>
            }
            
            <div className='push-notification-inner-container'>
                <DefaultHeading heading={translate(TranslationEnum.notification,"Add Notification")} showDefaultBackButton={!genericFilter || genericFilter.length===0} />
                <div>
                    <Autocomplete
                        disableClearable
                        className='add-notification-input-fields'
                        onChange={handleNotificationFeatureSelect}
                        value={notificationTarget}
                        options={notificationRedirectionOptions}
                        size="small"
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={translate(TranslationEnum.notification,"Select Feature")}
                            />
                        )}
                    />
                    <TextField
                        className='add-notification-input-fields'
                        id="outlined-start-adornment"
                        size="small"
                        value={heading}
                        label={translate(TranslationEnum.common_portal,"Heading")}
                        onChange={(event) => {
                            setHeading(event.target.value);
                        }}
                    />
                    <GenericFileUpload
                        resolution={{ width: 1024, height: 400 }} 
                        className="add-notification-input-fields" 
                        label="Upload Excel,PDF or Image of resolution"
                        file={notificationImage} setFile={setNotificationImage} 
                        defaultFile={notificationImageUrl} 
                        setDefaultFile={setNotificationImageUrl} 
                        accept={{
                            image: ["png","jpeg"],
                            application: ["pdf","vnd.ms-excel","vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
                            text: ["csv"]
                        }}
                    />
                    <div>
                    {translate(TranslationEnum.common_portal,"Description")}*
                    </div>
                    <ReactQuill
                        modules={{
                            toolbar:[
                                ['bold', 'italic', 'underline','strike'],
                                [{ 'color': [] }]
                            ]
                        }}
                        ref={(handleQuill)}
                        value={description} 
                        onChange={setDescription} 
                    />
                    <TextField
                        className='add-notification-input-fields'
                        id="outlined-start-adornment"
                        size="small"
                        hidden={notificationTarget.id !== "notification"}
                        value={mediaUrl}
                        label={translate(TranslationEnum.common_portal,"Media URL")}
                        onChange={(event) => {
                            setMediaUrl(event.target.value);
                        }}
                    />
                    <div className="notification-extra-container">
                        <NotificationAdditionalComponents feature={notificationTarget.id} additionalNotificationData={additionalNotificationData}/>
                    </div>

                    <div className='add-notification-send-button-container'>
                        <button className="add-notification-send-button" disabled={showSubmitLoader} onClick={() => setOpenConfirmPopup(true)}>
                            {showSubmitLoader ? (
                                <div>
                                    <CircularProgress size={15} color="inherit" />
                                    <span className="circular-progress-container ">{translate(TranslationEnum.common_portal,"Send").toUpperCase()}</span>
                                </div>
                            ) : (
                                <>{translate(TranslationEnum.common_portal,"Send").toUpperCase()}</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
            <ConfirmationPopUp openConfirmModal={openConfirmPopup} setOpenConfirmModal={setOpenConfirmPopup} successMethod={sendNotification} message="Are you sure want to send notification?"/>
        </div>
    )
}

export default NotificationForm;